import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  FormControl,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Modal,
  TextField,
  IconButton,
  Grid,
  Tooltip,
} from '@mui/material';
import * as MuiIcons from '@mui/icons-material';
import useCases from '../../../data/json/UseCase.json';
import backLogo from '../../../asset/images/carousel_bg_comingSoon.jpg';
import newbackLogo from '../../../asset/images/carousel_bg_industry_solutions.jpeg';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close'; // Import Close icon
import Header from '../../../shared/header/Header';
import appsBanner from '../../../asset/images/carousel_bg.jpg';
import { setAppsFilters } from '../../../reduxStore/slices/AppsFiltersSlice';
import { RootState } from '../../../reduxStore/store';
import { LogButtonEventToGA } from '../../../shared/googleAnalytics/GoogleAnalytics';
import {
  contactForDemoClickedText,
  goButtonClickedText,
  industryFilterChangedText,
  modalClosedText,
  // readMoreClickedText,
} from '../../../shared/googleAnalytics/GoogleAnalyticsConstants';

const iconMap: { [key: string]: React.ElementType } = {
  CommitIcon: MuiIcons.CheckCircleOutline,
  MessageIcon: MuiIcons.ChatBubbleOutline,
  OnlinePredictionIcon: MuiIcons.OnlinePrediction,
  AutomationIcon: MuiIcons.AutoMode,
  QueryStatsIcon: MuiIcons.QueryStats,
  DescriptionIcon: MuiIcons.Description,
  SummarizeIcon: MuiIcons.Summarize,
  DesignServicesIcon: MuiIcons.DesignServices,
  BuildIcon: MuiIcons.Build,
  RocketLaunchIcon: MuiIcons.RocketLaunch,
  AccountBalanceIcon: MuiIcons.AccountBalance,
  PipelineIcon: MuiIcons.DeviceHub, // Changed to a similar existing icon
  HubIcon: MuiIcons.Hub,
  PublicIcon: MuiIcons.Public,
};
interface ContactInfo {
  [key: number]: string;
}
interface ModalData {
  description: string;
  title: string;
}

function UseCase() {
  const dispatch = useDispatch();
  const { industries } = useSelector((state: RootState) => state.appsFilters.appsFilters);

  const [contactInfo, setContactInfo] = useState<ContactInfo>({});
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [modalContent, setModalContent] = useState<ModalData>({
    description: '',
    title: '',
  });
  const [searchTerm, setSearchTerm] = useState<string>('');

  const handleContactClick = (index: number) => {
    const useCase = filteredUseCases[index];
    const email =
      useCase.ownerEmail && useCase.ownerEmail.trim() !== '' ? useCase.ownerEmail : 'ai.support@wipro.com';
    LogButtonEventToGA(contactForDemoClickedText);
    setContactInfo((prev) => ({
      ...prev,
      [index]: prev[index] ? '' : `Contact us: <a href='mailto:${email}' style='margin-left: 5px;'>${email}</a>`,
    }));
  };

  const handleIndustryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.name;
    const updatedIndustries = industries.includes(value)
      ? industries.filter((industry) => industry !== value)
      : [...industries, value];

    dispatch(setAppsFilters({ industries: updatedIndustries }));
    LogButtonEventToGA(`${industryFilterChangedText} - ${value}`);
  };

  // const handleReadMoreClick = (description: string, title: string) => {
  //   LogButtonEventToGA(`${readMoreClickedText} - ${title}`);
  //   setModalContent({ description, title });
  //   setOpenModal(true);
  // };


  const formatAssetDescription = (desc: string) => {
    if (!desc) return null;
    const lines = desc.split(/\n|\u2022|•/).map(line => line.trim()).filter(Boolean);
    const linkWithDescRegex = /\[([^\]]+)\]\(([^)]+)\)\s*-\s*(.*)/;
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/;
    return (
      <>
        {lines.map((line, idx) => {
          const matchWithDesc = line.match(linkWithDescRegex);
          if (matchWithDesc) {
            return (
              <div key={idx} style={{ marginTop: idx === 0 ? 0 : 16, marginBottom: 10 }}>
                <a
                  href={matchWithDesc[2]}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#1976d2', fontWeight: 500, textDecoration: 'underline' }}
                >
                  {matchWithDesc[1]}
                </a>
                <span style={{ color: '#222', marginLeft: 8 }}>{matchWithDesc[3]}</span>
              </div>
            );
          }
          const match = line.match(linkRegex);
          if (match) {
            return (
              <div key={idx} style={{ marginTop: idx === 0 ? 0 : 16, marginBottom: 10 }}>
                <a
                  href={match[2]}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#1976d2', fontWeight: 500, textDecoration: 'underline' }}
                >
                  {match[1]}
                </a>
              </div>
            );
          }
          return (
            <div key={idx} style={{ marginTop: 16, marginBottom: 10, color: '#222' }}>{line}</div>
          );
        })}
      </>
    );
  };

  const handleAssetsClick = (assetDescription: string, title: string) => {
    setModalContent({ description: assetDescription, title: `${title}` });
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    LogButtonEventToGA(modalClosedText);
    setOpenModal(false);
    setModalContent({ description: '', title: '' });
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  // Filter use cases based on selected industries and search term
  const filteredUseCases = useCases.filter((useCase) => {
    const matchesSearchTerm = useCase.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesIndustry = industries.length === 0 || industries.includes(useCase.filter);
    return matchesSearchTerm && matchesIndustry;
  });

  return (
    <>
      <Header />
      <Box>
        <Box
          sx={{
            padding: '178px 96px 80px',
            backgroundImage: `url(${appsBanner})`,
            backgroundSize: 'cover', // Ensure the image covers the entire area
            backgroundPosition: 'center', // Center the image
            backgroundRepeat: 'no-repeat',
            display: 'flex',
            justifyContent: 'center', // Center the container horizontally
            alignItems: 'center', // Center the container vertically
            height: '100px',
          }}
        >
          {/* Left Content */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: 'auto',
              flexDirection: 'column',
              flex: 1,
              minWidth: 0,
            }}
          >
            <Typography variant="h4" component="h1" fontWeight={700} gutterBottom sx={{ color: 'white' }}>
              Discover apps built on AI
            </Typography>
            <Typography variant="body1" color="#fff" mb={3}>
              Experience seamless integration and advanced functionalities with apps built on Lab45 AI Platform.
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            display: { xs: 'block', sm: 'flex' },
            backgroundColor: '#f5f5f5',
            height: '100vh',
          }}
        >
          <Box
            sx={{
              width: { xs: '100%', sm: '250px' }, // Full width on small screens, fixed width on larger screens
              p: 2,
              marginTop: 4,
            }}
          >
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6" component="legend" align="left">
                  Domain / Industry
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ alignItems: 'flex-start' }}>
                <Divider sx={{ my: 2, width: '100%', marginTop: '-1rem' }} />
                <FormControl component="fieldset" sx={{ alignItems: 'flex-start', width: '100%' }}>
                  <FormGroup>
                    {Array.from(new Set(useCases.map((useCase) => useCase.filter))).map((industry) => (
                      <FormControlLabel
                        key={industry}
                        control={
                          <Checkbox
                            checked={industries.includes(industry)}
                            onChange={handleIndustryChange}
                            name={industry}
                            sx={{
                              padding: '0 8px 0 0', // Adjust padding to align with text
                            }}
                          />
                        }
                        label={
                          <Typography
                            sx={{
                              fontSize: '14px', // Ensure consistent font size
                              whiteSpace: 'nowrap', // Prevent text wrapping
                              textAlign: 'left', // Align text to the left
                              overflow: 'hidden', // Prevent overflow
                              textOverflow: 'ellipsis', // Add ellipsis if text is too long
                              lineHeight: '1.5', // Ensure consistent line height
                            }}
                          >
                            {industry}
                          </Typography>
                        }
                        sx={{
                          alignItems: 'center', // Align checkbox and label text vertically
                          margin: '8px 0', // Add vertical spacing between items
                        }}
                      />
                    ))}
                  </FormGroup>
                </FormControl>
              </AccordionDetails>
            </Accordion>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              padding: 2,
              alignItems: 'center',
            }}
          >
            <TextField
              label="Search Use Cases"
              variant="outlined"
              value={searchTerm}
              onChange={handleSearchChange}
              sx={{
                marginBottom: 2,
                maxWidth: { xs: '100%', sm: '600px' }, // Full width on small screens, max 600px on larger screens
                width: '100%',
              }}
            />

            <Grid
              container
              spacing={2}
              sx={{
                padding: { xs: '0', lg: '0 100px' },
                justifyContent: 'center',
              }}
            >
              {filteredUseCases.map((useCase, index) => {
                  const IconComponent = iconMap[useCase.icon] || MuiIcons.HelpOutline;
                  const newBackIndustries = [
                    'Cross-Industry Solutions',
                    'Health',
                    'BFSI',
                    'Technology & Communications',
                    'Engineering, Manufacturing',
                  ];
                  const useNewBackLogo = newBackIndustries.includes(useCase.filter);
                  return (
                    <Grid
                      key={index}
                      sx={{
                        marginBottom: '30px',
                        '&.MuiGrid-item': { paddingRight: '16px' },
                        width: { xs: '100%', sm: '384px', md: '280px' },
                        justifyContent: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                      }}
                    >
                      <Card
                        key={index}
                        sx={{
                          textAlign: 'center',
                          boxShadow: 3,
                          borderRadius: 3,
                          p: { xs: 1, sm: 2 },
                          bgcolor: '#ffffff',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          border: '1px solid #ddd',
                        }}
                      >
                        <Box
                          sx={{
                            width: '100%',
                            height: 100,
                            backgroundImage: `url(${useNewBackLogo ? newbackLogo : backLogo})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            borderTopLeftRadius: 12,
                            borderTopRightRadius: 12,
                          }}
                        />
                      <Box
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: '50%',
                          bgcolor: '#f0f0f0',
                          mx: 'auto',
                          mt: -3,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: 2,
                        }}
                      >
                        <IconComponent fontSize="large" sx={{ color: 'rgb(25, 118, 210)' }} />
                      </Box>

                      <CardContent sx={{ flexGrow: 1 }}>
                          <Typography fontWeight="bold" fontSize={16}>
                            {useCase.title.split('\n').map((line, idx) => (
                              <React.Fragment key={idx}>
                                {line}
                                {idx < useCase.title.split('\n').length - 1 && <br />}
                              </React.Fragment>
                            ))}
                          </Typography>
                        <Tooltip title={useCase.description} placement="top" arrow>
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            mt={1}
                            sx={{
                              display: '-webkit-box',
                              WebkitBoxOrient: 'vertical',
                              WebkitLineClamp: 3,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {useCase.description}
                          </Typography>
                        </Tooltip>
                        {useCase.description.split(' ').length > 10 && useCase.descriptionUrl !== '' && (
                          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                            <Button
                              size="small"
                              onClick={() => window.open(useCase.descriptionUrl, '_blank')}
                              sx={{
                                color: 'rgb(25, 118, 210)',
                                display: useCase.descriptionUrl ? 'block' : 'none',
                              }}
                            >
                              Read More
                            </Button>
                          </Box>
                        )}
                        {/* Assets Button */}
                        {useCase.assetDescription && (
                          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                            <Button
                              size="small"
                              variant="outlined"
                              sx={{ color: 'rgb(25, 118, 210)', borderColor: 'rgb(25, 118, 210)', borderRadius: 20 }}
                              onClick={() => handleAssetsClick(useCase.assetDescription, useCase.title)}
                            >
                              Assets
                            </Button>
                          </Box>
                        )}
                        <Box sx={{ display: contactInfo[index] ? 'block' : 'none', marginTop: '10px' }}>
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            dangerouslySetInnerHTML={{
                              __html: contactInfo[index] || '',
                            }}
                          />
                        </Box>
                      </CardContent>

                      <Box
                        className="use-case-card-footer"
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent={'center'}
                        gap={2}
                      >
                        <Button
                          variant="outlined"
                          sx={{
                            color: 'rgb(25, 118, 210)',
                            borderColor: 'rgb(25, 118, 210)',
                            borderRadius: 20,
                          }}
                          onClick={() => handleContactClick(index)}
                        >
                          Contact for demo
                        </Button>
                        <Button
                          variant="contained"
                          sx={{
                            bgcolor: 'rgb(25, 118, 210)',
                            color: '#fff',
                            borderRadius: 20,
                          }}
                          disabled={useCase.url === ''}
                          onClick={() => {
                            LogButtonEventToGA(`${goButtonClickedText} - ${useCase.title}`);
                            window.open(useCase.url, '_blank');
                          }}
                        >
                          Go
                        </Button>
                      </Box>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        </Box>
      </Box>

      <Modal open={openModal} onClose={handleCloseModal}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: 400 },
            bgcolor: 'background.paper',
            border: 0,
            boxShadow: 24,
            p: { xs: 2, sm: 4 },
          }}
        >
          <IconButton
            aria-label="close"
            onClick={handleCloseModal}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" component="h2">
            {modalContent.title}
          </Typography>
          <Typography sx={{ mt: 2 }} component="div">
            {formatAssetDescription(modalContent.description)}
          </Typography>
        </Box>
      </Modal>
    </>
  );
}

export default UseCase;
